import {Component, Element, h, Prop} from '@stencil/core';
import {AvatarColors, AvatarSizes, Sizes} from '@/types';

/**
 * If both photoUrl and fullName are empty, it will display an icon placeholder.
 */
@Component({
    tag: 'joy-avatar',
    styleUrl: 'avatar.scss',
    shadow: true,
})
export class JoyAvatar {
    @Element() el!: HTMLJoyAvatarElement;
    /**
     * Avatar size, 3 possible values (can't be overridden)
     */
    @Prop({reflect: true}) size?: AvatarSizes = 'medium';
    /**
     * Specify a photo url to display.
     */
    @Prop({reflect: true}) photoUrl?: string;
    /**
     * Freelancer full name. Will be used to display its initials if no photoUrl is given.
     */
    @Prop({reflect: true}) fullName?: string;
    /**
     * Specify a background color. Handy for avatars list. The value is the index of css custom property array made from CSS Custom Properties value (see next table)
     */
    @Prop({reflect: true}) color?: AvatarColors['freelancer'] = 'teal';
    /**
     * Specify the total amount of persons remaining from a list.
     */
    @Prop({reflect: true}) totalNumber?: number;
    /**
     * Specify the link to redirect to the full list
     */
    @Prop({reflect: true}) totalNumberLink?: string;
    /**
     * The prop is given automatically by avatars-list component when you set compress prop to true. You won't need this prop out of an avatars-list.
     */
    @Prop({reflect: true}) compress = false;
    /**
     * The prop is given automatically by avatars-list component when you set compress prop to true. You won't need this prop out of an avatars-list.
     */
    @Prop({reflect: true}) isFirstFromList = false;

    /** Url you need to link your avatar to */
    @Prop({reflect: true}) href?: string;

    /** Target attribute if href is used */
    @Prop({reflect: true}) target?: '_blank' | '_self' | '_parent' | '_top' | 'framename';

    /** Type of profile. Default or Strategy. Strategy mode is not compatible with compress mode layout. */
    @Prop() profile: 'strategy' | 'default' = 'default';

    get sizeClass() {
        return this.size ? {[`joy-avatar__${this.size}`]: true} : null;
    }

    private getFirstLetter(str: string) {
        const cleanedString = str.replace(/[';,:()]/g, '');
        return cleanedString.charAt(0);
    }

    private getInitials(): string {
        let arrayName = this.fullName!.split(' ');
        arrayName = arrayName.slice(0, 2);
        arrayName.forEach((str, idx) => {
            arrayName[idx] = this.getFirstLetter(str);
        });

        return arrayName.join('').toUpperCase();
    }

    get iconSize(): Sizes {
        if (this.size === 'large') {
            return 'medium';
        } else if (this.size === 'medium') {
            return 'small';
        } else if (this.size === 'small') {
            return 'xsmall';
        }
        return 'small';
    }

    get avatarClasses() {
        return {
            'joy-avatar': true,
            'joy-avatar__compress': this.compress,
            'joy-avatar__first': this.isFirstFromList,
            ...this.sizeClass,
            ...this.avatarColor,
        };
    }

    get avatarColor() {
        return {[`joy-avatar__${this.color}`]: true};
    }

    render() {
        /**
         * Photo is defined
         */
        if (this.photoUrl) {
            if (this.href) {
                return (
                    <a href={this.href} target={this.target ? this.target : ''}>
                        {this.avatarPicture}
                    </a>
                );
            } else {
                return this.avatarPicture;
            }
            /**
             * Photo is not defined but we want to display initials
             */
        } else if (!this.photoUrl && this.fullName) {
            if (this.href) {
                return <a href={this.href}>{this.initialsTemplate}</a>;
            } else {
                return this.initialsTemplate;
            }
            /**
             * Too many avatars so we display a "+ X" placeholder
             */
        } else if (!this.photoUrl && !this.fullName && this.totalNumber) {
            /**
             * The placeholder is a link to the full list
             */
            if (this.totalNumberLink) {
                return (
                    <a href={this.totalNumberLink} class={this.avatarClasses} target={this.target}>
                        + {this.totalNumber}
                    </a>
                );
                /**
                 * If no link to full list
                 */
            } else {
                return <div class={this.avatarClasses}>+{this.totalNumber}</div>;
            }
            /**
             * Simply display an user icon placeholder
             */
        } else {
            return (
                <div
                    class={{
                        ...this.avatarClasses,
                        'joy-avatar__placeholder': true,
                    }}
                >
                    <joy-icon name="user-photo" color="white" size={this.iconSize}></joy-icon>
                </div>
            );
        }
    }
    get avatarPicture() {
        return (
            <div class="joy-avatar__wrapper">
                <picture class={this.avatarClasses}>
                    <img src={this.photoUrl} title={this.fullName} alt="" loading="lazy" />
                </picture>
                {this.strategyTag}
            </div>
        );
    }

    get initialsTemplate() {
        return (
            <div class="joy-avatar__wrapper">
                <div class={this.avatarClasses} title={this.fullName}>
                    {this.getInitials()}
                </div>
                {this.strategyTag}
            </div>
        );
    }

    get strategyTag() {
        if (this.profile === 'default') {
            return;
        }

        return (
            <span class="joy-avatar__strategy">
                <svg width="12" height={this.size === 'small' ? '10' : '16'} viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M6.40531 15.88C5.29156 15.88 4.31531 15.6737 3.47656 15.2612C2.63781 14.8487 1.98469 14.2644 1.51719 13.5081C1.04969 12.7519 0.809061 11.8719 0.795311 10.8681L4.01281 10.3525C4.01281 11.2188 4.25344 11.9131 4.73469 12.4356C5.21594 12.9581 5.82781 13.2194 6.57031 13.2194C7.16156 13.2194 7.63594 13.0612 7.99344 12.745C8.36469 12.4288 8.55031 12.0231 8.55031 11.5281C8.55031 11.1156 8.40594 10.7719 8.11719 10.4969C7.84219 10.2081 7.38844 9.95375 6.75594 9.73375L4.96156 9.15625C3.65531 8.7575 2.70656 8.21438 2.11531 7.52687C1.53781 6.83937 1.24906 5.93187 1.24906 4.80437C1.24906 3.47062 1.68219 2.39812 2.54844 1.58687C3.41469 0.775624 4.63156 0.369999 6.19906 0.369999C7.65656 0.369999 8.83219 0.768749 9.72594 1.56625C10.6197 2.35 11.0803 3.41562 11.1078 4.76312L7.97281 5.36125C7.98656 4.64625 7.82156 4.07562 7.47781 3.64937C7.13406 3.20937 6.68719 2.98937 6.13719 2.98937C5.65594 2.98937 5.26406 3.14062 4.96156 3.44313C4.65906 3.73187 4.50781 4.10312 4.50781 4.55687C4.50781 4.99687 4.64531 5.36125 4.92031 5.65C5.20906 5.925 5.67656 6.15875 6.32281 6.35125L8.03469 6.92875C9.36844 7.3275 10.3309 7.87063 10.9222 8.55812C11.5272 9.23188 11.8297 10.1325 11.8297 11.26C11.8297 12.1538 11.6166 12.9513 11.1903 13.6525C10.7641 14.3538 10.1453 14.9038 9.33406 15.3025C8.52281 15.6875 7.54656 15.88 6.40531 15.88Z"
                        fill="#F7F7F5"
                    />
                </svg>
            </span>
        );
    }
}
